/* eslint-disable @typescript-eslint/no-explicit-any */
import { UUID } from "crypto";
import { baseApi } from "./baseApi";
import {
  setAccountUser,
  setCompanyData,
  setLoanApplication,
  setPersonalData,
  setShareHolderData,
  setLoanStatus,
  setAddressData,
  setShareHolderRfcCurp,
  setLegalRepresentative,
  setMotorbikeInformation,
  setShareholderIsLegalRep,
} from "@store/slices/registerSlice";
import { setProcessPage, setStatusConfig } from "@store/slices/appSlice";
import {
  PayloadRequestPhoneCode,
  PayloadValidatePhoneCode,
  PersonaStructure,
  SessionInfoResponse,
  StatusCatalog,
} from "@interfaces/index";
import { restoreBofuPfaeData } from "@store/slices/bofuSlicePfae";
import { TAX_SYSTEM_TYPE } from "@helpers/constantes";

const sessionApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    // `status_config` de un producto y tax system, en el formato del store (con `menu`/`submenu` del riel). Lo usa
    // DecisionIngreso al cambiar la solicitud al tax system de la vía elegida.
    getSessionStatusConfig: build.query<StatusCatalog[], { tax_system_id: string; product_id: string }>({
      query: ({ tax_system_id, product_id }) => ({
        url: "session_info/status_config",
        params: { tax_system_id, product_id },
      }),
      transformResponse: (response: { data: StatusCatalog[] }) => response.data,
    }),
    getSessionInfo: build.query<SessionInfoResponse, UUID | string>({
      query: (sso_id: UUID | string) => ({
        url: "session_info",
        params: {
          sso_id: sso_id,
        },
      }),
      transformResponse: (response: SessionInfoResponse) => {
        return response;
      },
      async onQueryStarted(_arg, api) {
        const { data } = await api.queryFulfilled;

        api.dispatch(setLoanStatus(data.data?.current_screen.code));
        api.dispatch(setProcessPage(data.data?.current_screen.url));
        api.dispatch(setLoanApplication(data.data?.loan_application_data));
        api.dispatch(setAccountUser(data.data?.user_data));
        api.dispatch(setPersonalData(data.data?.account_data));
        api.dispatch(setStatusConfig(data.data.status_config));

        if (data.data?.loan_application_data?.tax_system_type === TAX_SYSTEM_TYPE.PM.code) {
          api.dispatch(
            setCompanyData({
              id: data.data?.account_data?.id,
              name: data.data?.account_data?.company_name,
              rfc: data.data?.account_data?.company_rfc,
              legal_name: data.data?.account_data?.company_legal_name,
              role: "",
              ciec_pass: "",
              authorize_sat: false,
              is_correct_ciec: data.data?.loan_application_data.is_correct_ciec,
            }),
          );

          const shareholder_data = data.data?.shareholder_data;

          if (shareholder_data) {
            api.dispatch(setShareHolderData(shareholder_data));

            if (shareholder_data.rfc && shareholder_data.rfc.length > 10) {
              api.dispatch(
                setShareHolderRfcCurp({
                  rfc: shareholder_data.rfc.slice(0, 10),
                  rfc_homoclave: shareholder_data.rfc.slice(10, 13),
                  curp: shareholder_data.curp,
                }),
              );
            }

            if (shareholder_data.address) {
              const shareholder_address = shareholder_data.address;
              api.dispatch(
                setAddressData({
                  id: shareholder_address.id,
                  street: shareholder_address.street,
                  no_ext: shareholder_address.no_ext,
                  no_int: shareholder_address.no_int,
                  colony_id: shareholder_address.colonia_id,
                  state_id: +shareholder_address.state_id,
                  city_id: +shareholder_address.city_id,
                  locality_id: +shareholder_address.locality_id,
                  postal_code: shareholder_address.postal_code,
                  state_name: shareholder_address.state,
                  city_name: shareholder_address.municipality,
                  locality_name: shareholder_address.locality,
                  other_neighborhood: shareholder_address.other_neighborhood,
                }),
              );
            }
          }

          // El representante legal viene como una persona aparte del accionista. Si el backend regresa el
          // mismo registro en las dos (mismo `id`), es que el accionista mayoritario también es el
          // representante legal y el flujo se brinca sus 3 pantallas (`getLegalRepStatusToDiscard`).
          const legal_representative = data.data?.legal_representative;

          if (legal_representative) {
            api.dispatch(
              setLegalRepresentative({
                id: legal_representative.id,
                name: legal_representative.name,
                last_name: legal_representative.first_lastname,
                last_name_2: legal_representative.second_lastname,
                rfc: legal_representative.rfc,
                code_phone_number: legal_representative.code_phone_number,
                phone_number: legal_representative.phone_number,
                email: legal_representative.email,
                sex: legal_representative.sex,
                birth_date: legal_representative.date_birth,
                birth_state: legal_representative.birth_state ? Number(legal_representative.birth_state) : null,
                curp: legal_representative.curp,
              } as PersonaStructure),
            );
            api.dispatch(setShareholderIsLegalRep(legal_representative.id === shareholder_data?.id));
          }
        } else if (data.data?.loan_application_data?.tax_system_type === TAX_SYSTEM_TYPE.PFAE.code) {
          api.dispatch(
            setCompanyData({
              id: data.data?.account_data?.id,
              name: data.data?.account_data?.company_name,
              rfc: data.data?.account_data?.company_rfc,
              legal_name: data.data?.account_data?.company_legal_name,
              role: "",
              ciec_pass: "",
              authorize_sat: false,
              is_pfae: data.data?.account_data?.is_pfae,
              pfae_name: data.data?.account_data?.pfae_name,
              pfae_last_name: data.data?.account_data?.pfae_last_name,
              pfae_last_name_2: data.data?.account_data?.pfae_last_name_2,
              is_correct_ciec: data.data?.loan_application_data.is_correct_ciec,
              country_code_legal_representative: data.data?.account_data?.country_code_legal_representative,
              phone_legal_representative: data.data?.account_data?.phone_legal_representative,
              email_legal_representative: data.data?.account_data?.email_legal_representative,
            }),
          );

          const { shareholder_data } = data.data;

          if (shareholder_data) {
            api.dispatch(
              setLegalRepresentative({
                id: shareholder_data.id,
                name: shareholder_data.name,
                last_name: shareholder_data.first_lastname,
                last_name_2: shareholder_data.second_lastname,
                rfc: shareholder_data.rfc,
                code_phone_number: shareholder_data.code_phone_number,
                phone_number: shareholder_data.phone_number,
                email: shareholder_data.email,
                birth_date: shareholder_data.date_birth,
                birth_state: shareholder_data.birth_state ? Number(shareholder_data.birth_state) : null,
                curp: shareholder_data.curp,
              } as PersonaStructure),
            );
          }

          if (data.data.bofu_data)
            api.dispatch(
              restoreBofuPfaeData({
                files: data.data.bofu_data.company.files,
                bank_accounts: data.data.bofu_data.company.bank_accounts,
                transactional_info: data.data.bofu_data.company.loan_application.transactional_info,
                loan_application: data.data.bofu_data.company.loan_application,
              }),
            );
        } else if (data.data?.loan_application_data?.tax_system_type === TAX_SYSTEM_TYPE.PF.code) {
          const { shareholder_data, asset_data } = data.data;

          if (shareholder_data) {
            api.dispatch(
              setLegalRepresentative({
                id: shareholder_data.id,
                name: shareholder_data.name,
                last_name: shareholder_data.first_lastname,
                last_name_2: shareholder_data.second_lastname,
                rfc: shareholder_data.rfc,
                code_phone_number: shareholder_data.code_phone_number,
                phone_number: shareholder_data.phone_number,
                email: shareholder_data.email,
                birth_date: shareholder_data.date_birth,
                birth_state: shareholder_data.birth_state ? Number(shareholder_data.birth_state) : null,
                curp: shareholder_data.curp,
              } as PersonaStructure),
            );

            if (shareholder_data.address) {
              const { address } = shareholder_data;
              api.dispatch(
                setAddressData({
                  id: address.id,
                  street: address.street,
                  no_ext: address.no_ext,
                  no_int: address.no_int,
                  colony_id: address.colonia_id,
                  state_id: +address.state_id,
                  city_id: +address.city_id,
                  locality_id: +address.locality_id,
                  postal_code: address.postal_code,
                  state_name: address.state,
                  city_name: address.municipality,
                  locality_name: address.locality,
                  other_neighborhood: address.other_neighborhood,
                }),
              );
            }
          }

          if (asset_data) {
            api.dispatch(
              setMotorbikeInformation({
                motorbikeDetailId: asset_data.id,
                motorbikeCatalogId: asset_data.motorbike_catalog_id,
                details_platform: {
                  platform_id: asset_data.details_platform?.platform_id ?? "",
                  other_platform_name: asset_data.details_platform?.other_platform_name ?? "",
                  driver_id: asset_data.details_platform?.driver_id ?? "",
                },
              }),
            );
          }
        }
      },
    }),
    requestPhoneCodeLogin: build.mutation<Response, any>({
      query: (data: PayloadRequestPhoneCode) => ({
        url: `telefono/enviar_codigo_login`,
        method: "POST",
        body: data,
      }),
    }),
    validatePhoneCodeLogin: build.mutation<Response, any>({
      query: (data: PayloadValidatePhoneCode) => ({
        url: `telefono/validar_codigo_login`,
        method: "POST",
        body: data,
      }),
    }),
  }),
});

export const {
  useLazyGetSessionInfoQuery,
  useLazyGetSessionStatusConfigQuery,
  useRequestPhoneCodeLoginMutation,
  useValidatePhoneCodeLoginMutation,
} = sessionApi;
